import { darken, getContrastRatio, lighten, type PaletteColor } from "@mui/material/styles";
import { NEUTRAL, SECONDARY_GOLD } from "./tokens";

export const DEFAULT_PRIMARY_COLOR = "#7B77AA";

/** The four brand color slots the Appearance dialog can override independently. */
export type ColorRole = "primary" | "secondary" | "tertiary" | "neutral";

const HEX_REGEX = /^#([0-9a-fA-F]{6})$/;

export const isValidHexColor = (value: string): boolean => HEX_REGEX.test(value);

/** Compares two hex colors, ignoring letter case. */
export const isSameHexColor = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

export type ShadeKey = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

export type ShadeMap = Record<ShadeKey, string>;

const SHADE_LIGHTEN: Record<number, number> = {
	50: 0.92,
	100: 0.82,
	200: 0.66,
	300: 0.5,
	400: 0.28,
};

const SHADE_DARKEN: Record<number, number> = {
	600: 0.12,
	700: 0.26,
	800: 0.42,
	900: 0.58,
};

// WCAG AA Large threshold (3.0) — appropriate for buttons / 18pt+ text.
// Using 4.5 (AA body) here is too strict and flips most brand-purple buttons
// to dark text, which looks bad against the saturated background.
const CONTRAST_THRESHOLD = 3.0;

export const buildShadeMap = (baseHex: string): ShadeMap => {
	const safe = isValidHexColor(baseHex) ? baseHex : DEFAULT_PRIMARY_COLOR;
	return {
		50: lighten(safe, SHADE_LIGHTEN[50]),
		100: lighten(safe, SHADE_LIGHTEN[100]),
		200: lighten(safe, SHADE_LIGHTEN[200]),
		300: lighten(safe, SHADE_LIGHTEN[300]),
		400: lighten(safe, SHADE_LIGHTEN[400]),
		500: safe,
		600: darken(safe, SHADE_DARKEN[600]),
		700: darken(safe, SHADE_DARKEN[700]),
		800: darken(safe, SHADE_DARKEN[800]),
		900: darken(safe, SHADE_DARKEN[900]),
	};
};

export const pickContrastText = (background: string): string => (getContrastRatio(background, "#FFFFFF") >= CONTRAST_THRESHOLD ? "#FFFFFF" : "#1A1A2E");

/** A brand color for a non-text indicator on `surface`: `main`, else `dark`, else `fallback` — the first to reach 3:1. */
export const accessibleIndicatorColor = (color: Pick<PaletteColor, "main" | "dark">, surface: string, fallback: string): string =>
	[color.main, color.dark].find((candidate) => getContrastRatio(candidate, surface) >= CONTRAST_THRESHOLD) ?? fallback;

/**
 * A fully-derived palette color (main + light/dark stops + readable contrast
 * text + the 50–900 shade ladder). Shaped to spread straight into a MUI
 * `palette.<role>` entry alongside the augmented shade keys.
 */
export interface ColorPalette {
	main: string;
	light: string;
	dark: string;
	contrastText: string;
	shades: ShadeMap;
}

/**
 * Generic palette builder for any brand color role (secondary, tertiary,
 * neutral). Derives light/dark from the shade ladder and picks a readable
 * contrast text. `primary` uses `buildPrimaryPalette` instead — it carries
 * hand-picked default-brand stops.
 */
export const buildColorPalette = (baseHex: string): ColorPalette => {
	const shades = buildShadeMap(baseHex);
	const main = shades[500];
	return {
		main,
		light: shades[300],
		dark: shades[700],
		contrastText: pickContrastText(main),
		shades,
	};
};

export interface PrimaryPalette extends ColorPalette {
	hover: string;
	active: string;
}

/**
 * Hand-picked overrides for the default brand color. The lighten/darken pair
 * applied to #7B77AA produces shades that don't match the prod header
 * gradient — these are the exact stops the design uses (#A699DA → #4F517A).
 */
const DEFAULT_LIGHT = "#A699DA";
const DEFAULT_DARK = "#4F517A";

export const buildPrimaryPalette = (baseHex: string): PrimaryPalette => {
	const base = buildColorPalette(baseHex);
	const isDefault = baseHex.toLowerCase() === DEFAULT_PRIMARY_COLOR.toLowerCase();
	return {
		...base,
		// The default brand color's lighten/darken stops don't match the prod
		// header gradient — override with the exact design stops.
		light: isDefault ? DEFAULT_LIGHT : base.light,
		dark: isDefault ? DEFAULT_DARK : base.dark,
		hover: base.shades[600],
		active: base.shades[700],
	};
};

// DEFAULT: app-baseline color for each non-primary role. Independent-controls
// mode keeps today's identity as the reset target
export const DEFAULT_SECONDARY_COLOR = DEFAULT_PRIMARY_COLOR;
export const DEFAULT_TERTIARY_COLOR = SECONDARY_GOLD;
export const DEFAULT_NEUTRAL_COLOR = NEUTRAL[300];

export const DEFAULT_COLOR_BY_ROLE: Record<ColorRole, string> = {
	primary: DEFAULT_PRIMARY_COLOR,
	secondary: DEFAULT_SECONDARY_COLOR,
	tertiary: DEFAULT_TERTIARY_COLOR,
	neutral: DEFAULT_NEUTRAL_COLOR,
};

// UKG brand dark teal. Also the primary color of the UKG department look.
export const UKG_DARK_TEAL = "#005151";

// Curated preset colors. Picked for sufficient contrast against white text.
export interface ThemePreset {
	id: string;
	labelKey: string;
	value: string;
}

export const THEME_PRESETS: readonly ThemePreset[] = [
	{ id: "default", labelKey: "appearance.preset.default", value: DEFAULT_PRIMARY_COLOR },
	{ id: "indigo", labelKey: "appearance.preset.indigo", value: "#3F51B5" },
	{ id: "blue", labelKey: "appearance.preset.blue", value: "#1976D2" },
	{ id: "teal", labelKey: "appearance.preset.teal", value: "#00897B" },
	{ id: "green", labelKey: "appearance.preset.green", value: "#2E7D32" },
	{ id: "amber", labelKey: "appearance.preset.amber", value: "#B26A00" },
	{ id: "orange", labelKey: "appearance.preset.orange", value: "#E65100" },
	{ id: "red", labelKey: "appearance.preset.red", value: "#C62828" },
	{ id: "pink", labelKey: "appearance.preset.pink", value: "#C2185B" },
	{ id: "graphite", labelKey: "appearance.preset.graphite", value: "#37474F" },
	{ id: "ukg-teal", labelKey: "appearance.preset.ukg.teal", value: "#3AD6C5" },
	{ id: "ukg-dark-teal", labelKey: "appearance.preset.ukg.darkTeal", value: UKG_DARK_TEAL },
	{ id: "bryte-blue", labelKey: "appearance.preset.bryte.blue", value: "#29C2DE" },
	{ id: "bryte-purple", labelKey: "appearance.preset.bryte.purple", value: "#59178A" },
	{ id: "bryte-fuchsia", labelKey: "appearance.preset.bryte.fuchsia", value: "#B92485" },
] as const;
